import { Download, Package } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { api } from '@api'
import { TOKEN_AUTH } from '@auth/token.ts'
import { SettingsRow } from '@components/SettingsRow.tsx'
import { useOnline } from '@hooks/useOnline.ts'
import { useResource } from '@hooks/useResource.ts'
import styles from './Settings.module.css'

const formatBytes = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`
	const kb = bytes / 1024
	if (kb < 1024) return `${Math.round(kb)} KB`
	return `${(kb / 1024).toFixed(1)} MB`
}

const formatClock = (iso: string): string =>
	new Date(iso).toLocaleTimeString(undefined, {
		hour: '2-digit',
		minute: '2-digit',
	})

const WHAT_YOU_GET =
	'A zip of spreadsheets — your household, its members, your recipes, ' +
	'activities, meal plan and the todos you can see — plus every photo ' +
	'you have uploaded.'

const WHILE_BUILDING =
	'This will continue if you close the page — come back in a minute for ' +
	'your download link.'

// The whole section is a function of one server-owned job row, so there's no
// local lifecycle to keep in sync — close the tab mid-build, come back, and
// the row still says what's happening. useResource pauses polling while the
// tab is hidden and refetches on focus, which is what makes that work.
export const ExportSection = () => {
	// useResource can't read its own result, so "is a build in flight" has to
	// live outside it. One extra render when the status flips, in exchange for
	// not polling every three seconds for as long as Settings is open.
	const online = useOnline()
	const [building, setBuilding] = useState(false)
	const [starting, setStarting] = useState(false)
	const [opening, setOpening] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const { data, set, refetch } = useResource(
		['export-job'],
		() => api.account.export.status(),
		{ pollIntervalMs: building ? 3000 : undefined },
	)

	const job = data?.job ?? null
	const status = job?.status
	const expiresAt = job?.expiresAt ?? null

	useEffect(() => {
		setBuilding(status === 'pending')
	}, [status])

	// A ready export dies on the hour. Without this, a page left open would go
	// on offering a Download link that 404s; instead it refetches at the
	// moment of expiry and the Export button comes back on its own.
	useEffect(() => {
		if (status !== 'ready' || expiresAt === null) return
		const ms = Date.parse(expiresAt) - Date.now()
		if (ms <= 0) {
			void refetch()
			return
		}
		const id = window.setTimeout(() => void refetch(), ms)
		return () => window.clearTimeout(id)
	}, [status, expiresAt, refetch])

	const start = async () => {
		setStarting(true)
		setError(null)
		try {
			set(await api.account.export.start())
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Could not start export')
		} finally {
			setStarting(false)
		}
	}

	// iOS build only. A link can't carry the Bearer token, so fetch the
	// presigned URL first. Capacitor opens an off-app URL in Safari, which
	// saves the zip.
	const openDownload = async () => {
		setOpening(true)
		setError(null)
		try {
			const { url } = await api.account.export.link()
			window.location.assign(url)
		} catch (e) {
			setError(
				e instanceof Error ? e.message : 'Could not open the download',
			)
		} finally {
			setOpening(false)
		}
	}

	const exportButton = (label: string) => (
		<button
			type="button"
			onClick={() => void start()}
			disabled={starting || !online}
		>
			{starting ? 'Starting…' : label}
		</button>
	)

	const view = (): {
		value: string
		action: ReactNode
		hint: string | null
	} => {
		if (data === undefined) {
			return { value: 'Checking…', action: null, hint: null }
		}
		if (job === null || job.status === 'failed') {
			return {
				value:
					job === null
						? 'Ready when you are'
						: "That export didn't finish",
				action: exportButton(
					job === null ? 'Export data' : 'Try again',
				),
				hint: WHAT_YOU_GET,
			}
		}
		if (job.status === 'pending') {
			return {
				value: 'Preparing your download…',
				action: null,
				hint: WHILE_BUILDING,
			}
		}
		const size =
			job.sizeBytes === null ? '' : ` (${formatBytes(job.sizeBytes)})`
		return {
			value: `Ready${size}`,
			action: TOKEN_AUTH ? (
				<button
					type="button"
					onClick={() => void openDownload()}
					disabled={opening || !online}
				>
					<Download size={15} aria-hidden /> Download
				</button>
			) : (
				<a href={api.account.export.downloadUrl}>
					<Download size={15} aria-hidden /> Download
				</a>
			),
			hint:
				job.expiresAt === null
					? null
					: `Your link expires at ${formatClock(job.expiresAt)}.`,
		}
	}

	const { value, action, hint } = view()

	return (
		<section className={styles.section}>
			<h2 className={styles.sectionTitle}>Your data</h2>
			<SettingsRow
				icon={<Package size={18} />}
				label="Export"
				value={value}
				action={action}
				hint={
					error ? <span className={styles.error}>{error}</span> : hint
				}
			/>
		</section>
	)
}
